<script>

export default defineComponent({
  name: 'ReservationTour',
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const site = useSiteStore()

    const availabilitiesPending = ref(false)
    const noAvailabilities = ref(false)
    const availabilities = ref([])
    const selectedAvailability = ref(null)
    const selectedAvailabilityId = ref(null)
    const participants = ref(props.product.variations.map(variation => ({
      title: variation.name,
      description: variation.description,
      key: variation.externalId,
      quantity: variation.externalId === 'r1' ? 1 : 0
    })));

    const {
      date,
      time,
      performances,
      availableDates,
      disabledDates,
      minDate,
      maxDate,
      hasTickets,
      timeOptions,
      performancesPending
    } = usePerformances(props.product)

    const addToCartRequest = ref({
      productId: props.product?.id,
      performanceId: null,
      variations: [],
    })

    const setCartRequest = () => {
      addToCartRequest.value.performanceId = time.value?.value;
      addToCartRequest.value.variations = [
        {
          id: selectedAvailability.value.id,
          quantity: 1,
          total_customers: selectedAvailability.value.total_customers,
        }
      ]
      ableToAddToCart.value = true
    }

    const checkForAvailabilityRequest = computed(() => {
      return {
          productId: props.product?.id,
          performanceId: time.value?.value,
          date: date.value,
          queryParams: Object.fromEntries(
            participants.value
              .filter(participant => participant.quantity > 0)
              .map(participant => [participant.key, participant.quantity])
          )
      }
    });

    watch(checkForAvailabilityRequest, (newVal, oldVal) => {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        ableToAddToCart.value = false
        selectedAvailability.value = null
        noAvailabilities.value = false
      }
    });
    const ableToCheckAvailability = computed(() => {
      return date.value !== null && time.value !== null
    });
    const ableToAddToCart = ref(false)

    const pendingPage = computed(() => {
      return performancesPending.value || availabilitiesPending.value;
    })

    function buildAddressAndUrl(point) {
      let address = '';
      let url = ''

      if (point.address1) {
        address += point.address1;
      }
      if (point.address2) {
        if (address) address += ', ';
        address += point.address2;
      }
      if (point.postcode) {
        if (address) address += ', ';
        address += point.postcode;
      }
      if (point.geocode) {
        url = `https://www.google.com/maps/?q=${point.geocode}`;
      }

      if (url && address) {
        return `<a href="${url}" target="_blank">${address}</a>`
      }

      return undefined
    }

    const selectAvailability = (availabilityId) => {
      selectedAvailability.value = availabilities.value.find((a) => a.id === availabilityId)
      setCartRequest()
    }

    const onStartFetchingAvailabilities = () => {
      availabilitiesPending.value = true
    }

    const onFinishFetchingAvailabilities = (availabilitiesData) => {
      availabilities.value = availabilitiesData.items.map(availability => ({
        id: availability.variationId,
        price: availability.price,
        name: availability.name,
        total_customers: availability.details.total_customers,
        selectedPickupPoint: null,
        pickupPoints: (availability.details?.pickup_points || []).map((point) => {
          return {
            label: point.pickup_name,
            value: point.pickup_key,
            link: buildAddressAndUrl(point),
          }
        }),
      }));

      if (availabilities.value.length >= 1) {
        selectedAvailability.value = availabilities.value[0];
        selectedAvailabilityId.value = availabilities.value[0].id;
        setCartRequest()
      } else {
        noAvailabilities.value  = true
      }
      availabilitiesPending.value = false
    }

    const handlePickupPointSelection = () => {
      addToCartRequest.value.variations[0]['pickup_key'] = selectedAvailability.value.selectedPickupPoint.value;
    }

    return {
      site,
      hasTickets,
      disabledDates,
      pendingPage,
      minDate,
      maxDate,
      date,
      time,
      participants,
      timeOptions,
      availabilities,
      selectedAvailability,
      ableToCheckAvailability,
      noAvailabilities,
      onFinishFetchingAvailabilities,
      onStartFetchingAvailabilities,
      handlePickupPointSelection,
      selectAvailability,
      selectedAvailabilityId,
      ableToAddToCart,
      addToCartRequest,
      checkForAvailabilityRequest,
    }

  }
})

</script>

<template>
  <AppPaper class="app-tickets-attraction relative p-3 pt-6 md:p-16">
    <template v-if="hasTickets">
      <div class="row relative z-20 mb-6">
        <div class="mb-6 col-md-7">
          <h3>{{ $t('string.Date') }}</h3>

          <AppSelectDate
            v-model="date"
            :label="$t('string.Add-date')"
            full-width
            :disabled-dates="disabledDates"
            :min-date="minDate"
            :max-date="maxDate"
          />
        </div>

        <div v-if="product.hasPerformances" class="col-md-5">
          <h3>{{ $t('string.Time') }}</h3>

          <AppSelect
            v-model="time"
            :title="$t('string.Add-time')"
            class="w-full"
            :options="timeOptions"
            full-width
            :class="{
              'pointer-events-none opacity-50':
                !date || !product.hasPerformances,
            }"
          ></AppSelect>
        </div>
      </div>
      <section class="mb-12">
        <header>
          <p>{{ $t('component.tickets.transport.participants.description') }}</p>
        </header>


        <div class="p-4">
          <template
            v-for="(participant, index) in participants"
            :key="participant.key"
          >
            <article class="app-tickets-attraction__category">
              <header>
                <h4>{{ participant.title }}</h4>

                <p v-if="participant.description">{{ participant.description }}</p>
              </header>

              <p class="app-tickets-attraction__category__price"></p>

              <AppInputQuantity
                v-model="participant.quantity"
                :min="participant.key === 'r1' ? 1 : 0"
              />
            </article>

            <AppDivider v-if="index < participants.length - 1" class="my-3" />
          </template>
        </div>
      </section>
      <template v-if="ableToAddToCart && availabilities?.length > 1">
        <h3>{{$t('string.Availabilities')}}</h3>
        <AppRadioGroup
          v-model="selectedAvailabilityId"
          @update:modelValue="selectAvailability"
          :items="
          availabilities.map((item) => ({
            label:`${item.name} - <strong>${formatCurrency(item.price)}</strong>`,
            value: item.id,
          }))
        "
        />
      </template>
      <div v-if="availabilities.length === 0 && noAvailabilities" class="app-text-field--error">{{$t('string.No-Availabilities')}}</div>
      <AppSelect
        v-if="ableToAddToCart && selectedAvailability.pickupPoints.length > 0"
        v-model="selectedAvailability.selectedPickupPoint"
        :title="$t('string.Pickup-point')"
        :options="selectedAvailability.pickupPoints"
        icon="map"
        :hint="selectedAvailability.selectedPickupPoint?.link"
        @update:modelValue="handlePickupPointSelection"
        full-width
        class="w-full z-10 mb-6"
      ></AppSelect>
      <CheckForAvailabilitiesButton
        v-if="ableToCheckAvailability && !ableToAddToCart"
        :criteria="checkForAvailabilityRequest"
        @on-availability-fetch-start="onStartFetchingAvailabilities"
        @on-availability-fetch-finish="onFinishFetchingAvailabilities"
      />
      <AddToCartButton
        v-if="ableToAddToCart"
        :data="addToCartRequest"
        :disabled="selectedAvailability.pickupPoints.length > 0 && !selectedAvailability.selectedPickupPoint"
        :total-price="selectedAvailability.price"
      />
      <Transition name="fade">
        <div
          v-if="pendingPage"
          class="text-red absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-white/75"
        >
          <AppSpinner :style="{ fontSize: '2.5rem' }" />
        </div>
      </Transition>
    </template>
    <p
      v-else
      v-html="
        $t('page.product.unavailable.description', {
          email: site.data?.emailAddress,
        })
      "
    ></p>
  </AppPaper>
</template>

<style scoped lang="scss">

</style>
